import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../cn.js';
/**
 * The icon set (§05).
 *
 * One grid, one stroke weight, round caps and joins, no fills except where the
 * fill carries meaning. Drawn rather than imported: six shapes that must never
 * change do not justify a dependency that can.
 *
 * The six status glyphs are locked to their meanings in tokens.json — check is
 * verified, clock is scheduled, pulse is in progress, triangle is needs review,
 * octagon is blocking, minus is inactive. An icon shape never means two things,
 * and `status-signals.spec.ts` fails the build if two ever collide.
 */
const STATUS_PATHS = {
    check: _jsx("path", { d: "M3 8.5l3.5 3.5L13 5" }),
    clock: (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("path", { d: "M8 4.6V8l2.4 1.6" })] })),
    pulse: _jsx("path", { d: "M1.5 8h3l2-4 2.6 8 2-4h3.4" }),
    triangle: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M8 2.2L14.6 13.4H1.4z" }), _jsx("path", { d: "M8 6.6v3.1" }), _jsx("circle", { cx: "8", cy: "11.6", r: ".9", fill: "currentColor", stroke: "none" })] })),
    octagon: (_jsxs(_Fragment, { children: [_jsx("path", { d: "M5.4 1.6h5.2l3.8 3.8v5.2l-3.8 3.8H5.4L1.6 10.6V5.4z" }), _jsx("path", { d: "M8 5v3.6" }), _jsx("circle", { cx: "8", cy: "11.2", r: ".9", fill: "currentColor", stroke: "none" })] })),
    minus: _jsx("path", { d: "M4 8h8" }),
};
/**
 * A status glyph.
 *
 * `data-glyph` is not decoration — it is what the grayscale test reads to prove
 * that the shape, and not the colour, is carrying the meaning.
 */
export function StatusGlyph({ name, className }) {
    return (_jsx("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false", "data-glyph": name, className: cn('h-4 w-4 flex-none', className), fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: STATUS_PATHS[name] }));
}
function Glyph({ className, children }) {
    return (_jsx("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false", className: cn('h-4 w-4 flex-none', className), fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: children }));
}
export const ChevronDown = ({ className }) => (_jsx(Glyph, { className: className, children: _jsx("path", { d: "M4 6.5 8 10.5 12 6.5" }) }));
export const ArrowRight = ({ className }) => (_jsx(Glyph, { className: className, children: _jsx("path", { d: "M3 8h10M9 4l4 4-4 4" }) }));
export const CloseMark = ({ className }) => (_jsx(Glyph, { className: className, children: _jsx("path", { d: "M4 4l8 8M12 4l-8 8" }) }));
/**
 * The loading spinner.
 *
 * The one rotating element in the operational product, and it only ever appears
 * while a request is genuinely in flight — §05's line between motion that
 * reports a true system state and motion that decorates. `prefers-reduced-motion`
 * stops it; the label beside it does not depend on it.
 */
export const Spinner = ({ className }) => (_jsxs("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false", className: cn('h-4 w-4 flex-none animate-spin', className), fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", children: [_jsx("circle", { cx: "8", cy: "8", r: "6", opacity: ".25" }), _jsx("path", { d: "M14 8a6 6 0 0 0-6-6" })] }));
//# sourceMappingURL=icons.js.map